import { Button, PatientsDropdown, SidePanel, AssignToggle } from "components";
import AssignmentFields from "pages/AppPages/TherapistPages/SinglePatient/components/PatientWorkbook/components/AssignmentDetails/AssignmentFields";
import React, { useContext, useEffect } from "react";
import ReactTooltip from "react-tooltip";
import { useTranslation } from "react-i18next";
import { useAddAssignment, useUpdateTemplateSubscription } from "utils/hooks";
import useOrganizationSettings, {
  getPatientWord,
} from "utils/hooks/organizations/useOrganizationSettings";
import CurrentAssignmentContext from "../CurrentAssignmentContext";
import useAssignmentLibraryUtils from "../useAssignmentLibraryUtils";
import { truncateText } from "utils/helpers";
import { Template } from "types";

const AssignmentPreviewSidePanel = () => {
  const {
    currentNotification,
    patientHasAssignment,
    currentPatient,
    setCurrentPatient,
    formatPatientToSelect,
    patients,
    hasPatientSlug,
    currentGroup,
    group,
    assignChecked,
    setAssignChecked,
    shareWithOrg,
    setShareWithOrg,
    user,
    isAssignmentOwner,
  } = useAssignmentLibraryUtils();

  const { currentAssignment, setCurrentAssignment } = useContext(
    CurrentAssignmentContext
  );
  const { addAssignment, addGroupAssignment } = useAddAssignment();
  const { updateTemplateSubscription, loading, isUpdateSuccessful } =
    useUpdateTemplateSubscription();
  const { t } = useTranslation("common");
  const { settings: orgSettings } = useOrganizationSettings();

  useEffect(() => {
    if (isUpdateSuccessful) {
      if (isUpdateSuccessful) {
        setCurrentAssignment(null);
        setAssignChecked(false);
      }
    }
  }, [isUpdateSuccessful, setAssignChecked, setCurrentAssignment]);

  const handleAssignmentTypeClicked = (assignment: any) => {
    currentGroup
      ? addGroupAssignment({
          args: {
            actionPlanId: null,
            type: assignment.type,
            notification: currentNotification,
            templateUUID: assignment.uuid,
            groupId: currentGroup?.uuid ?? null,
          } as any,
          groupSlug: currentGroup?.slug,
          origin: "workbook",
        })
      : addAssignment({
          args: {
            actionPlanId: null,
            patientId: currentPatient?.uuid ?? null,
            type: assignment.type,
            notification: currentNotification,
            templateUUID: assignment.uuid,
          } as any,
          patientSlug: currentPatient?.slug,
          origin: "workbook",
        });
    setCurrentAssignment(null);
  };

  const handlePatientChange = (patientId: string) => {
    const newPatient = patients.find(
      (patient: any) => patient.uuid === patientId
    );
    setCurrentPatient(newPatient);
  };

  const handleAssignToNewPatientsSubmit = async (template: Template) => {
    await updateTemplateSubscription({
      templateId: template.uuid,
      defaultTemplate: assignChecked,
      shareWithOrganization: shareWithOrg,
    });
  };

  return (
    <SidePanel
      title={
        <div className="assignment-preview-panel-tabs">
          <h4 className="side-panel__header-title">
            {currentAssignment?.label || currentAssignment?.title}
          </h4>
          <div className="preview-tag">{t("common_labels.preview")}</div>
        </div>
      }
      isVisible={currentAssignment}
      onClose={() => setCurrentAssignment(null)}
    >
      <div
        className={`assignment-preview-fields-wrapper ${
          patients?.length > 0 ? "assignment-preview-fields-wrapper-longer" : ""
        } ${
          currentPatient
            ? "assignment-preview-fields-wrapper-longer-extended"
            : ""
        }`}
        data-tip
        data-for="preview-tip"
      >
        <AssignmentFields assignment={currentAssignment?.content} />
      </div>
      <div
        className="assignment-preview-actions"
        data-tip
        data-for={`${currentAssignment?.type}-tip`}
      >
        {!currentPatient && !currentGroup && (
          <AssignToggle
            checked={assignChecked}
            label="calls_to_action.assign_to_new_patients"
            onChange={() => setAssignChecked(!assignChecked)}
            extraClass="assign-toggle"
          />
        )}
        {user?.activeOrganizationId && (
          <AssignToggle
            checked={shareWithOrg}
            label={t("organizations.add_to_practice_resources_label")}
            extraClass="assign-toggle"
            disabled={!isAssignmentOwner}
            onChange={() => setShareWithOrg(!shareWithOrg)}
          />
        )}
        {patients?.length > 0 &&
          !hasPatientSlug &&
          currentAssignment?.default === assignChecked && (
            <div className="assignment-preview-patient-select">
              <PatientsDropdown
                name="selectedPatient"
                includeEmail={false}
                placeholder={t("common_labels.choose_patient", {
                  patientWord: getPatientWord(orgSettings),
                })}
                onChange={(v) => {
                  handlePatientChange(v?.value);
                }}
                hideSelectedOptions={false}
                value={
                  currentPatient ? formatPatientToSelect(currentPatient) : null
                }
                menuPlacement="top"
              />
            </div>
          )}
        {(currentPatient || currentGroup) && (
          <Button
            name="add_assignment_button"
            value={t("calls_to_action.assign_and_continue", {
              name: currentGroup
                ? truncateText(currentGroup.name)
                : truncateText(
                    currentPatient?.firstName ?? currentPatient?.email
                  ),
            })}
            extraClass="primary pill"
            onClick={() => handleAssignmentTypeClicked(currentAssignment)}
          />
        )}
        {(currentAssignment?.default !== assignChecked ||
          currentAssignment?.shareWithOrganization !== shareWithOrg) &&
          !currentPatient &&
          !currentGroup && (
            <Button
              name="assign_button"
              value={
                assignChecked
                  ? t("calls_to_action.assign")
                  : t("common_labels.save_button")
              }
              extraClass="primary pill"
              isLoading={loading}
              onClick={() => handleAssignToNewPatientsSubmit(currentAssignment)}
            />
          )}
        {patientHasAssignment(currentAssignment?.type) && (
          <ReactTooltip id={`${currentAssignment?.type}-tip`} place="top">
            {group?.name ?? currentPatient?.firstName ?? currentPatient.email}{" "}
            already has a {currentAssignment?.label || currentAssignment?.title}{" "}
            assigned to {group ? "it" : "them"}.
          </ReactTooltip>
        )}

        <ReactTooltip id="preview-tip" place="left">
          This is a preview and cannot be filled in or submitted
        </ReactTooltip>
      </div>
    </SidePanel>
  );
};

export default AssignmentPreviewSidePanel;
